import { useState, useRef, useCallback, useEffect, lazy, Suspense } from 'react';
import { useChat } from '../chat-provider';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { useFileUpload } from '../hooks/use-file-upload';
import { useSpeechRecognition } from '../hooks/use-speech-recognition';
import { CapabilityPicker } from './capability-picker';
import { cn } from '@/lib/utils';
import {
    Send, Square, Paperclip, Mic, MicOff, X, AlertCircle, Settings, Loader2,
} from 'lucide-react';

const AgentDetailPage = lazy(() => import('@/pages/agents/agent-detail-page'));

export function ChatInput() {
    const { sendMessage, stopStreaming, status, selectedCapability } = useChat();
    const [text, setText] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const isStreaming = status === 'streaming' || status === 'submitted';

    const [settingsOpen, setSettingsOpen] = useState(false);

    const agentAppKey = selectedCapability
        ? selectedCapability.slice(0, selectedCapability.lastIndexOf('/'))
        : undefined;
    const agentName = selectedCapability
        ? selectedCapability.slice(selectedCapability.lastIndexOf('/') + 1).split(':')[0]
        : undefined;

    const {
        files, isDragging, inputRef: fileInputRef,
        addFiles, removeFile, clearFiles, openFilePicker,
        handleDragEnter, handleDragLeave, handleDragOver, handleDrop, handlePaste,
    } = useFileUpload();

    const handleSpeechTranscript = useCallback((transcript: string) => {
        setText((prev) => prev + transcript);
    }, []);

    const { isListening, isSupported: speechSupported, interimTranscript, error: speechError, toggle: toggleSpeech } =
        useSpeechRecognition({ onTranscript: handleSpeechTranscript });

    const handleSubmit = useCallback(() => {
        if (!text.trim() || isStreaming) return;
        sendMessage(text);
        setText('');
        clearFiles();
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    }, [text, isStreaming, sendMessage, clearFiles]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
            }
        },
        [handleSubmit],
    );

    const handleInput = () => {
        const el = textareaRef.current;
        if (el) {
            el.style.height = 'auto';
            el.style.height = `${Math.min(el.scrollHeight, window.innerHeight * 0.5)}px`;
        }
    };

    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    const validFiles = files.filter((f) => !f.error);
    const errorFiles = files.filter((f) => f.error);

    return (
        <div
            className="shrink-0 border-t border-slate-100 p-2 md:p-4 dark:border-slate-700"
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
        >
            {isDragging && (
                <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-blue-400 bg-blue-50/80 dark:bg-blue-950/80">
                    <p className="text-sm font-medium text-blue-600">Drop files here</p>
                </div>
            )}

            {interimTranscript && (
                <div className="mb-2 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
                    <Mic className="mr-1 inline h-3 w-3" />
                    {interimTranscript}
                </div>
            )}

            {speechError && (
                <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                    <AlertCircle className="mr-1 inline h-3 w-3" />
                    {speechError}
                </div>
            )}

            {errorFiles.length > 0 && (
                <div className="mb-2 space-y-1">
                    {errorFiles.map((f) => (
                        <div key={f.id} className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
                            <AlertCircle className="h-3 w-3 shrink-0" />
                            <span className="flex-1 truncate">{f.file.name}: {f.error}</span>
                            <button onClick={() => removeFile(f.id)} className="text-amber-500 hover:text-amber-700">
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {validFiles.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-2">
                    {validFiles.map((f) => (
                        <div key={f.id} className="flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 p-1.5 text-xs dark:border-blue-700 dark:bg-blue-950">
                            {f.preview ? (
                                <img src={f.preview} alt="" className="h-8 w-8 rounded object-cover" />
                            ) : (
                                <Paperclip className="h-3 w-3 text-blue-500" />
                            )}
                            <span className="max-w-[120px] truncate text-blue-800 dark:text-blue-200">{f.file.name}</span>
                            <button onClick={() => removeFile(f.id)} className="text-blue-400 hover:text-blue-600">
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <form
                onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
                className="relative flex flex-col rounded-xl border border-slate-200 bg-slate-50 transition-colors focus-within:border-blue-300 focus-within:ring-1 focus-within:ring-blue-200 dark:border-slate-700 dark:bg-slate-800"
            >
                <div className="flex items-end gap-2 px-3 py-2">
                    <button
                        type="button"
                        onClick={openFilePicker}
                        className="mb-0.5 rounded p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700"
                        title="Attach file"
                    >
                        <Paperclip className="h-4 w-4" />
                    </button>
                    <textarea
                        ref={textareaRef}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        onInput={handleInput}
                        onPaste={handlePaste}
                        placeholder={
                            selectedCapability
                                ? 'Type a message...'
                                : 'Select a capability first...'
                        }
                        disabled={!selectedCapability}
                        rows={1}
                        aria-label="Chat message input"
                        className={cn(
                            'max-h-[50vh] min-h-[36px] flex-1 resize-none bg-transparent text-sm text-slate-900 placeholder-slate-400',
                            'transition-[height] duration-150 ease-out focus:outline-none',
                            'dark:text-slate-100 dark:placeholder-slate-500',
                            'disabled:cursor-not-allowed disabled:opacity-50',
                        )}
                        style={{ height: 'auto', minHeight: '36px' }}
                    />
                </div>

                <div className="flex items-center justify-between border-t border-slate-100 px-3 py-1.5 dark:border-slate-700">
                    <div className="flex items-center gap-1">
                        <CapabilityPicker />
                        {selectedCapability && (
                            <button
                                type="button"
                                onClick={() => setSettingsOpen(true)}
                                className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700"
                                title="Agent settings"
                            >
                                <Settings className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        {speechSupported && (
                            <button
                                type="button"
                                onClick={toggleSpeech}
                                className={cn(
                                    'rounded p-1.5 transition-colors',
                                    isListening
                                        ? 'bg-red-100 text-red-500 dark:bg-red-900'
                                        : 'text-slate-400 hover:bg-slate-200 hover:text-slate-600 dark:hover:bg-slate-700',
                                )}
                                title={isListening ? 'Stop listening' : 'Voice input'}
                            >
                                {isListening ? (
                                    <MicOff className="h-4 w-4 animate-pulse" />
                                ) : (
                                    <Mic className="h-4 w-4" />
                                )}
                            </button>
                        )}

                        {isStreaming ? (
                            <button
                                type="button"
                                onClick={stopStreaming}
                                className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-500 text-white transition-colors hover:bg-red-600"
                                title="Stop generation"
                            >
                                <Square className="h-3 w-3" />
                            </button>
                        ) : (
                            <button
                                type="submit"
                                disabled={!text.trim() || !selectedCapability}
                                className={cn(
                                    'flex h-7 w-7 items-center justify-center rounded-lg transition-colors',
                                    text.trim() && selectedCapability
                                        ? 'bg-blue-600 text-white hover:bg-blue-500'
                                        : 'bg-slate-200 text-slate-400 dark:bg-slate-700 dark:text-slate-500',
                                )}
                                title="Send message"
                            >
                                <Send className="h-3.5 w-3.5" />
                            </button>
                        )}
                    </div>
                </div>
            </form>

            <p className="mt-2 text-center text-[10px] text-slate-400">
                AI can make mistakes. Verify important information.
            </p>

            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                multiple
                onChange={(e) => {
                    if (e.target.files) addFiles(e.target.files);
                    e.target.value = '';
                }}
            />

            <SlideDrawer
                isOpen={settingsOpen && !!agentAppKey && !!agentName}
                onClose={() => setSettingsOpen(false)}
            >
                <div className="flex items-center justify-end px-6 pb-2 pt-4 shrink-0">
                    <button
                        type="button"
                        onClick={() => setSettingsOpen(false)}
                        className="rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600"
                    >
                        <X size={20} />
                    </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto">
                    {agentAppKey && agentName && (
                        <Suspense fallback={
                            <div className="flex h-full items-center justify-center">
                                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                            </div>
                        }>
                            <AgentDetailPage
                                appKey={agentAppKey}
                                name={agentName}
                            />
                        </Suspense>
                    )}
                </div>
            </SlideDrawer>
        </div>
    );
}
